import { useMemo, useState } from 'react'
import { Search, Users } from 'lucide-react'
import { useAssignableUsers } from '@/lib/queries'
import { useDebounced } from '@/lib/useDebounced'
import { Button, Checkbox, EmptyState, Input, Modal, Spinner } from '@/components/ui'
import { plural } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'
import type { TranslationKey } from '@/i18n/dictionary'

/**
 * `plural`, over the three keys that spell one counted noun.
 *
 * Typing the parameters rather than casting at the call site is what keeps all
 * three checked against the dictionary; the cast only puts back what `plural`
 * widened, since it returns one of these three arguments. The same helper sits
 * in SubmissionDetail.tsx — the shared home would be lib/utils.ts, which
 * `shadcn init` rewrites.
 */
function pluralKey(
  count: number,
  one: TranslationKey,
  few: TranslationKey,
  many: TranslationKey,
): TranslationKey {
  return plural(count, one, few, many) as TranslationKey
}

/**
 * Picks the people who were working a centre.
 *
 * A dialog rather than an inline list, because this sits on a screen somebody is
 * filling in while walking a machine: the checklist below it is up to 73 rows,
 * and a second long list stacked above it pushes the actual work off the phone.
 * The trigger shows the count so the choice is still visible without opening it.
 *
 * Search is debounced and filters locally — the directory is one plant's staff,
 * a few dozen people, so fetching it once and filtering in memory beats a
 * request per keystroke on plant wifi.
 */
export function WorkerPicker({
  value,
  onChange,
  disabled,
  workCenterId,
}: {
  value: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
  /**
   * The machine being evaluated.
   *
   * Given one, the list opens on the people assigned to it. A 5S walk happens
   * at a single machine, and scrolling sixty-five names on a phone to tick the
   * three people standing there is the problem this solves.
   */
  workCenterId?: string
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debounced = useDebounced(search, 200)

  /*
   * The escape hatch. Somebody covering a shift on another line will not be in
   * the assignment list, and a picker that cannot reach them is worse than one
   * that starts too wide.
   */
  const [showEveryone, setShowEveryone] = useState(false)

  // Only fetched once the dialog is opened: most 5S walks never touch it.
  const directory = useAssignableUsers(open, showEveryone ? undefined : workCenterId)

  // Memoised because `?? []` is a fresh array on every render, so the two
  // filters below listed a dependency that always changed and memoised nothing.
  const people = useMemo(() => directory.data?.data ?? [], [directory.data])

  // `all` also comes back when the machine simply has nobody assigned, which is
  // why the notice below is worded as a fact rather than as a filter state.
  const narrowed = directory.data?.scope === 'workCenter'

  const filtered = useMemo(() => {
    const needle = debounced.trim().toLowerCase()
    if (!needle) return people

    return people.filter(
      (person) =>
        person.name.toLowerCase().includes(needle) ||
        person.employeeCode.toLowerCase().includes(needle),
    )
  }, [people, debounced])

  /*
   * Selected people first, so somebody who has picked five out of forty can see
   * their choice without scrolling — and so unticking is as easy as ticking.
   */
  const ordered = useMemo(() => {
    const chosen = new Set(value)

    return [...filtered].sort((a, b) => {
      const diff = Number(chosen.has(b.id)) - Number(chosen.has(a.id))

      return diff !== 0 ? diff : a.name.localeCompare(b.name, 'bs')
    })
  }, [filtered, value])

  const chosenNames = people.filter((p) => value.includes(p.id)).map((p) => p.name)

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  }

  return (
    <>
      <Button
        variant="secondary"
        fullWidth
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="justify-start"
      >
        <Users className="size-4" aria-hidden />
        {value.length === 0
          ? t('submissions.workers.choose')
          : t(
              pluralKey(
                value.length,
                'submissions.workers.count.one',
                'submissions.workers.count.few',
                'submissions.workers.count.many',
              ),
              { count: value.length },
            )}
      </Button>

      {/* The names themselves, so the choice survives closing the dialog. */}
      {chosenNames.length > 0 && (
        <p className="muted mt-1.5 text-xs">{chosenNames.join(', ')}</p>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('submissions.workers.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => onChange([])} disabled={value.length === 0}>
              {t('submissions.workers.clear')}
            </Button>
            <Button onClick={() => setOpen(false)}>{t('submissions.workers.done')}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="muted text-sm">{t('submissions.workers.hint')}</p>

          <div className="relative">
            <Search
              className="muted pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('submissions.workers.search.placeholder')}
              className="pl-9"
              type="search"
              aria-label={t('submissions.workers.search.label')}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>

          {/*
            Shown only when the list really is narrowed, and worded as a fact
            about the machine rather than as a filter that is switched on — the
            server also returns everyone when a machine has nobody assigned, and
            claiming "filtered" there would be a lie.
          */}
          {narrowed && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[var(--surface-muted,rgba(0,0,0,0.03))] px-3 py-2 dark:bg-white/5">
              <p className="muted text-xs">{t('submissions.workers.narrowed')}</p>
              <button
                type="button"
                onClick={() => setShowEveryone(true)}
                className="text-xs font-medium underline underline-offset-2"
              >
                {t('submissions.workers.showEveryone')}
              </button>
            </div>
          )}

          {directory.isLoading && <Spinner label={t('submissions.workers.loading')} />}

          {directory.isError && (
            <EmptyState
              title={t('submissions.workers.unavailable')}
              description={(directory.error as Error).message}
            />
          )}

          {directory.data && ordered.length === 0 && (
            <EmptyState
              title={t('submissions.workers.noResults')}
              description={t('submissions.workers.noResults.description')}
            />
          )}

          {ordered.length > 0 && (
            <ul className="max-h-72 divide-y divide-[var(--border-subtle)] overflow-y-auto rounded-xl border border-[var(--border-subtle)]">
              {ordered.map((person) => (
                <li key={person.id} className="px-3">
                  <Checkbox
                    checked={value.includes(person.id)}
                    onChange={() => toggle(person.id)}
                    label={
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{person.name}</span>
                        <span className="muted block truncate text-xs">
                          {person.employeeCode} · {person.roleLabel}
                        </span>
                      </span>
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>
    </>
  )
}
